/**
 * @file Runs a CSV export in the background and tracks its progress, stop and reset controls.
 */
import { useState, useRef } from "react";
import { usePapaParse } from "react-papaparse";

import authenticate from "@components/Authorization/authenticate/authenticate";
import authorizeUser from "@components/Authorization/authorize/authorizeUser.js";
import { useAppContext } from "@components/AppContext";
import { downloadContent } from "@components/Import";
import type { AppContext } from "@components/AppContext/types";

const CSV_EXTENSION = "csv";
const CSV_CONTENT_TYPE = "text/csv";

const PROGRESS_START = 0;
const PROGRESS_TOTAL = 100;

type Row = Record<string, unknown>;

type ExportHelpers = {
  shouldStop: () => boolean;
  updateProgress: (currentIndex: number, totalCount: number) => void;
  authorizeOrganization: (organizationId?: string) => Promise<string>;
};

type GetRows = (helpers: ExportHelpers, ...args: unknown[]) => Promise<Row[] | undefined>;

type Options = {
  onReady: () => void;
  getRows: GetRows;
  fileName: string;
};

type Start = (...args: unknown[]) => Promise<void>;

type ExportProgress = [
  number | null,
  Start,
  () => void,
  () => void,
];

/**
 * Runs the given row-fetching export as a CSV download and exposes its progress, stop and reset controls.
 */
const useExportProgress = ({ onReady, fileName, getRows }: Options): ExportProgress => {
  const shouldStopRef = useRef(false);

  const [ progress, setProgress ] = useState<number | null>(null);

  const { jsonToCSV } = usePapaParse();
  const { showErrorMessage } = useAppContext() as AppContext;

  /**
   * Authenticates the current user and authorizes them against the given organization.
   */
  const authorizeOrganization = async (organizationId?: string): Promise<string> => {
    const isBackstage = true;
    const shouldRefresh = true;

    const authenticationJwt = await authenticate(showErrorMessage, isBackstage, shouldRefresh);
    const [ authorization ] = await authorizeUser(authenticationJwt, organizationId);

    return authorization;
  };

  /**
   * Updates the export progress percentage from the current row index and the total row count.
   */
  const updateProgress = (currentIndex: number, totalCount: number): void => {
    const percent = Math.ceil(currentIndex * PROGRESS_TOTAL / totalCount);

    setProgress(percent);
  };

  /**
   * Reports whether the running export must stop.
   */
  const shouldStop = (): boolean => shouldStopRef.current;

  /**
   * Fetches the export rows, then downloads them as a CSV file unless the export was stopped.
   */
  const start: Start = async (...args) => {
    setProgress(PROGRESS_START);
    shouldStopRef.current = false;

    const rows = await getRows({ authorizeOrganization, shouldStop, updateProgress }, ...args);

    const isStopped = shouldStopRef.current;

    if (isStopped) {
      return;
    }

    const content = jsonToCSV(rows);
    const shouldTimestamp = true;

    downloadContent(`${fileName}.${CSV_EXTENSION}`, content, CSV_CONTENT_TYPE, shouldTimestamp);
    onReady();
  };

  /**
   * Signals the running export to stop.
   */
  const stop = (): void => {
    shouldStopRef.current = true;
  };

  /**
   * Clears the progress state so a new export can start.
   */
  const reset = (): void => {
    setProgress(null);
    shouldStopRef.current = false;
  };

  return [ progress, start, stop, reset ];
};

export default useExportProgress;
